import axios from "axios";
import {
  getAccessToken,
  triggerRefresh,
  clearAccessToken,
  getRecoveryToken,
} from "./tokenService";
import {
  isCdnUrl,
  isBackend,
  shouldSkipTokenInjection,
  shouldSkip401Refresh,
  isResetPasswordEndpoint,
} from "./urlHelpers";

export {
  isCdnUrl,
  isBackend,
  shouldSkipTokenInjection,
  shouldSkip401Refresh,
  isResetPasswordEndpoint,
} from "./urlHelpers";

const fetchAdapter = async (config) => {
  const url = config.url;
  const method = (config.method || "get").toUpperCase();
  const headers = {};

  if (config.headers) {
    if (typeof config.headers.toJSON === "function") {
      Object.assign(headers, config.headers.toJSON());
    } else {
      Object.entries(config.headers).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          headers[k] = v;
        }
      });
    }
  }

  let body = config.data;
  if (
    body !== undefined &&
    body !== null &&
    typeof body === "object" &&
    !(body instanceof FormData) &&
    !(body instanceof Blob)
  ) {
    body = JSON.stringify(body);
    const hasContentType = Object.keys(headers).some(
      (k) => k.toLowerCase() === "content-type"
    );
    if (!hasContentType) {
      headers["Content-Type"] = "application/json";
    }
  }

  const fetchOptions = {
    method,
    headers,
    body,
    signal: config.signal,
  };

  if (config.withCredentials && !isCdnUrl(url)) {
    fetchOptions.credentials = "include";
  } else if (isCdnUrl(url)) {
    fetchOptions.credentials = "omit";
  }

  const fetchFn =
    (typeof global !== "undefined" && global.fetch) ||
    (typeof window !== "undefined" && window.fetch);

  if (typeof fetchFn !== "function") {
    throw new Error("No fetch implementation available in environment");
  }

  const response = await fetchFn(url, fetchOptions);

  const getHeader = (name) => {
    if (!response.headers) return null;
    if (typeof response.headers.get === "function") {
      return response.headers.get(name);
    }
    const lower = name.toLowerCase();
    for (const [k, v] of Object.entries(response.headers)) {
      if (k.toLowerCase() === lower) return v;
    }
    return null;
  };

  const contentType = (getHeader("content-type") || "").toLowerCase();
  const isBinaryContentType =
    contentType.startsWith("image/") ||
    contentType.startsWith("audio/") ||
    contentType.startsWith("video/") ||
    contentType.includes("application/pdf") ||
    contentType.includes("application/octet-stream") ||
    contentType.includes("application/zip") ||
    contentType.includes("spreadsheetml") ||
    contentType.includes("wordprocessingml");

  let data;
  if (!response.ok) {
    try {
      if (typeof response.text === "function") {
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : text;
        } catch {
          data = text;
        }
      } else if (typeof response.json === "function") {
        data = await response.json();
      } else {
        data = null;
      }
    } catch {
      data = null;
    }
  } else if (config.responseType === "arraybuffer") {
    data = typeof response.arrayBuffer === "function" ? await response.arrayBuffer() : response;
  } else if (config.responseType === "blob" || isBinaryContentType) {
    data = typeof response.blob === "function" ? await response.blob() : response;
  } else if (config.responseType === "text") {
    data = typeof response.text === "function" ? await response.text() : String(response);
  } else {
    try {
      if (typeof response.text === "function") {
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : text;
        } catch {
          data = text;
        }
      } else if (typeof response.json === "function") {
        data = await response.json();
      } else {
        data = response.data !== undefined ? response.data : response;
      }
    } catch {
      data = response.data !== undefined ? response.data : response;
    }
  }

  const resHeaders = {};
  if (response.headers) {
    if (typeof response.headers.forEach === "function") {
      response.headers.forEach((v, k) => {
        resHeaders[k] = v;
      });
    } else if (typeof response.headers.get === "function") {
      resHeaders["content-disposition"] = response.headers.get("content-disposition");
    }
  }

  const axiosResponse = {
    data,
    status: response.status ?? 200,
    statusText: response.statusText ?? "OK",
    headers: resHeaders,
    config,
  };

  if (response.ok === false) {
    const error = new Error(
      (typeof data === "object" && (data?.detail || data?.message)) ||
        (typeof data === "string" && data) ||
        `Request failed with status code ${response.status}`
    );
    error.config = config;
    error.response = axiosResponse;
    throw error;
  }

  return axiosResponse;
};

const apiClient = axios.create({
  withCredentials: true,
  ...(process.env.NODE_ENV === "test" ? { adapter: fetchAdapter } : {}),
});

apiClient.interceptors.request.use(
  (config) => {
    try {
      const url = config.url || "";
      const token = isResetPasswordEndpoint(url) ? getRecoveryToken() : getAccessToken();

      if (!isBackend(url) || isCdnUrl(url)) {
        config.withCredentials = false;
      }

      if (token && isBackend(url) && !shouldSkipTokenInjection(url)) {
        config.headers = config.headers || {};
        const hasAuth = Object.keys(config.headers).some(
          (k) => k.toLowerCase() === "authorization"
        );
        if (!hasAuth) {
          if (typeof config.headers.set === "function") {
            config.headers.set("Authorization", `Bearer ${token}`);
          } else {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
        }
      }

      if (url.includes("/api/v1/answer-engine/chat-request")) {
        config.metadata = {
          questionTimestamp: new Date().toISOString(),
        };
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("apiClient auth inject failed:", err);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

export const __resetApiClientForTesting = () => {
  failedQueue.forEach((prom) => {
    try {
      prom.reject(new Error("Test reset"));
    } catch {
      // Ignore
    }
  });
  failedQueue = [];
  isRefreshing = false;
};

const processQueue = (err, token = null) => {
  failedQueue.forEach((prom) => {
    if (err) prom.reject(err);
    else prom.resolve(token);
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    const url = response?.config?.url || "";
    if (url.includes("/api/v1/answer-engine/chat-request")) {
      const questionTimestamp =
        response.config?.metadata?.questionTimestamp || new Date().toISOString();
      const answerTimestamp = new Date().toISOString();

      let requestPayload = response.config?.data;
      if (typeof requestPayload === "string") {
        try {
          requestPayload = JSON.parse(requestPayload);
        } catch (e) {
          // Keep string if parse fails
        }
      }

      const responseData = response.data;

      window.dispatchEvent(
        new CustomEvent("ae-widget:chat-request-completed", {
          detail: {
            requestPayload,
            responseData,
            questionTimestamp,
            answerTimestamp,
          },
        })
      );
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const url = originalRequest.url || "";

    if (url.includes("/api/v1/answer-engine/chat-request")) {
      const questionTimestamp =
        originalRequest.metadata?.questionTimestamp || new Date().toISOString();
      const answerTimestamp = new Date().toISOString();

      let requestPayload = originalRequest.data;
      if (typeof requestPayload === "string") {
        try {
          requestPayload = JSON.parse(requestPayload);
        } catch (e) {
          // Keep string
        }
      }

      let responseData = error.response?.data;
      if (!responseData) {
        responseData = {
          success: false,
          error:
            error.message ||
            `HTTP ${error.response?.status}: ${error.response?.statusText}`,
          status: error.response?.status,
          statusText: error.response?.statusText,
        };
      } else if (typeof responseData === "string") {
        responseData = {
          success: false,
          error: responseData,
          status: error.response?.status,
          statusText: error.response?.statusText,
        };
      } else if (typeof responseData === "object" && !("success" in responseData)) {
        responseData = { ...responseData, success: false };
      }

      window.dispatchEvent(
        new CustomEvent("ae-widget:chat-request-completed", {
          detail: {
            requestPayload,
            responseData,
            questionTimestamp,
            answerTimestamp,
          },
        })
      );
    }

    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      isBackend(url) &&
      !shouldSkip401Refresh(url)
    ) {
      // eslint-disable-next-line no-console
      console.error(
        `[${new Date().toISOString()}] Received 401 response, attempting token refresh.`
      );

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            if (typeof originalRequest.headers.set === "function") {
              originalRequest.headers.set("Authorization", `Bearer ${token}`);
            } else {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await triggerRefresh();
        processQueue(null, newToken);
        originalRequest.headers = originalRequest.headers || {};
        if (typeof originalRequest.headers.set === "function") {
          originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
        } else {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearAccessToken();
        window.dispatchEvent(new CustomEvent("auth:logout-redirect"));
        return Promise.reject(error.response ? error : refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Authenticated streaming fetch for SSE and ReadableStream endpoints.
 * Injects Authorization Bearer tokens, sets default credentials, and coordinates
 * reactive 401 token refresh while preserving native Response and ReadableStream.
 */
export const apiStreamFetch = async (url, options = {}) => {
  const token = isResetPasswordEndpoint(url) ? getRecoveryToken() : getAccessToken();
  const headers = {};

  if (options.headers) {
    if (typeof options.headers.forEach === "function") {
      options.headers.forEach((v, k) => {
        headers[k] = v;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // 1. Inject token for backend endpoints
  if (
    token &&
    isBackend(url) &&
    !shouldSkipTokenInjection(url) &&
    !headers["Authorization"] &&
    !headers["authorization"]
  ) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // 2. Default credentials (omit for CDN to satisfy CORS, include for backend)
  const isCdn = isCdnUrl(url);
  const fetchOptions = {
    ...options,
    headers,
    credentials: isCdn ? "omit" : (options.credentials || "include"),
  };

  const fetchFn =
    (typeof global !== "undefined" && global.fetch) ||
    (typeof window !== "undefined" && window.fetch);

  if (typeof fetchFn !== "function") {
    throw new Error("No fetch implementation available in environment");
  }

  let response = await fetchFn(url, fetchOptions);

  // 3. Reactive 401 token refresh & single retry
  if (
    response.status === 401 &&
    !options._retry &&
    isBackend(url) &&
    !shouldSkip401Refresh(url)
  ) {
    try {
      const newToken = await triggerRefresh();
      headers["Authorization"] = `Bearer ${newToken}`;
      response = await fetchFn(url, { ...fetchOptions, headers, _retry: true });
    } catch (refreshErr) {
      clearAccessToken();
      window.dispatchEvent(new CustomEvent("auth:logout-redirect"));
      throw refreshErr;
    }
  }

  return response;
};

apiClient.streamFetch = apiStreamFetch;

export default apiClient;
